/**
 * Password protection, as library operations every client runs the same way.
 *
 * Protecting makes a *new* map — the shell, holding only the envelope — and moves the original to
 * the Trash. A new map rather than an edit, because the original's journal remembers every word it
 * ever held, and no edit to it could make those entries forget. Removing the protection is the
 * mirror image: a plain copy, and the shell to the Trash.
 *
 * Files are the one thing that cannot be protected: they are stored and synced as they are, beside
 * the map. A map with files is refused, with that reason, rather than protected with holes in it.
 *
 * The platform supplies the cipher; the refusals come back as reasons, and each client words them.
 */
import { createDocument, isSealed, resealDocument, sealDocument, sealedShell, unsealDocument, SEALED_KEY, } from '@orbit-mind/domain';
import { unwrap } from '@orbit-mind/shared-types';
import { renameMap } from './intents';
import { readLibraryDocument } from './library-actions';
export class ProtectionRefused extends Error {
    reason;
    constructor(reason) {
        super(reason);
        this.reason = reason;
        this.name = 'ProtectionRefused';
    }
}
function hasFiles(document) {
    return Object.values(document.nodes).some((node) => (node.attachments?.length ?? 0) > 0);
}
/** The document under another map id: every topic and link moved across, nothing renamed. */
export function moveDocument(document, mapId, at) {
    const nodes = {};
    for (const [id, node] of Object.entries(document.nodes))
        nodes[id] = { ...node, mapId };
    const edges = {};
    for (const [id, edge] of Object.entries(document.edges))
        edges[id] = { ...edge, mapId };
    return {
        map: { ...document.map, id: mapId, createdAt: at, updatedAt: at, deletedAt: null },
        nodes: nodes,
        edges: edges,
    };
}
/** Protects a map. Returns the protected copy's id; the original goes to the Trash. */
export async function protectLibraryMap(input) {
    const source = await readLibraryDocument(input.journal, input.mapId);
    if (isSealed(source))
        throw new ProtectionRefused('already-protected');
    if (hasFiles(source))
        throw new ProtectionRefused('has-files');
    const { envelope } = await sealDocument(source, input.password, input.cipher, {
        ...(input.hint === undefined ? {} : { hint: input.hint }),
    });
    const at = Date.now();
    const shell = sealedShell(createDocument({
        mapId: input.newMapId,
        rootNodeId: input.newRootId,
        workspaceId: source.map.workspaceId,
        ownerId: source.map.ownerId,
        now: at,
        title: source.map.title,
    }), envelope, source.map.title);
    unwrap(await input.catalog.create(shell, at));
    unwrap(await input.catalog.moveToTrash(input.mapId, at));
    return shell.map.id;
}
/** Removes the protection: a plain copy of the map, and the shell to the Trash. */
export async function unprotectLibraryMap(input) {
    const shell = await readLibraryDocument(input.journal, input.mapId);
    const opened = await unsealDocument(shell, input.password, input.cipher);
    if (!opened.ok)
        throw new ProtectionRefused(opened.reason);
    const at = Date.now();
    const plain = moveDocument(opened.document, input.newMapId, at);
    unwrap(await input.catalog.create(plain, at));
    unwrap(await input.catalog.moveToTrash(input.mapId, at));
    return plain.map.id;
}
/**
 * Writes an open protected map back into its shell: a fresh envelope on the shell's root, and the
 * name, if it changed, on the shell's map — the one thing the library shows.
 *
 * Not an undo step on the shell: the edits being saved already have theirs, in the engine in
 * memory the person is working in.
 */
async function persistSealedDocument(real, document, session, cipher) {
    const envelope = await resealDocument(document, session, cipher);
    const shell = real.document();
    unwrap(await real.dispatch({
        kind: 'update-node',
        payload: {
            nodeId: shell.map.rootNodeId,
            changes: { metadata: { [SEALED_KEY]: envelope } },
        },
        meta: real.nextMeta(),
    }, { recordHistory: false }));
    const title = document.map.title.trim();
    if (title !== '' && title !== shell.map.title) {
        unwrap(await real.dispatch(renameMap(real.context(), title), { recordHistory: false }));
    }
}
// Serialize encryption and dispatch together: an older envelope must never overwrite a newer edit.
const sealedWrites = new WeakMap();
export function saveSealedDocument(real, document, session, cipher) {
    const pending = (sealedWrites.get(real) ?? Promise.resolve())
        .catch(() => undefined)
        .then(() => persistSealedDocument(real, document, session, cipher));
    sealedWrites.set(real, pending);
    void pending
        .finally(() => {
        if (sealedWrites.get(real) === pending)
            sealedWrites.delete(real);
    })
        .catch(() => undefined);
    return pending;
}
//# sourceMappingURL=sealed-actions.js.map